import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { gymManagementService } from '../../lib/gymManagementService';
import GymCard from './components/GymCard';
import GymTable from './components/GymTable';
import GymEditModal from './components/GymEditModal';
import GymCreateModal from './components/GymCreateModal';
import GymStatistics from './components/GymStatistics';

const GymManagementCenter = () => {
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'inactive'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGym, setSelectedGym] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    loadGyms();
    loadStatistics();
  }, []);

  const loadGyms = async () => {
    setLoading(true);
    const { data, error } = await gymManagementService?.getAllGyms();
    if (!error && data) {
      setGyms(data);
    }
    setLoading(false);
  };

  const loadStatistics = async () => {
    const { data, error } = await gymManagementService?.getGymStatistics();
    if (!error && data) {
      setStatistics(data);
    }
  };

  const handleEditGym = (gym) => {
    setSelectedGym(gym);
    setIsEditModalOpen(true);
  };

  const handleCreateGym = () => {
    setIsCreateModalOpen(true);
  };

  const handleSaveGym = async (gymData) => {
    if (selectedGym) {
      const { error } = await gymManagementService?.updateGym(selectedGym?.id, gymData);
      if (!error) {
        await loadGyms();
        await loadStatistics();
        setIsEditModalOpen(false);
        setSelectedGym(null);
      }
    }
  };

  const handleCreateNewGym = async (gymData) => {
    const { error } = await gymManagementService?.createGym(gymData);
    if (!error) {
      await loadGyms();
      await loadStatistics();
      setIsCreateModalOpen(false);
    }
  };

  const handleToggleStatus = async (gymId, currentStatus) => {
    const { error } = await gymManagementService?.toggleGymStatus(gymId, !currentStatus);
    if (!error) {
      await loadGyms();
      await loadStatistics();
    }
  };

  const filteredGyms = gyms?.filter(gym => {
    const matchesStatus = 
      filterStatus === 'all' ? true :
      filterStatus === 'active' ? gym?.is_active :
      !gym?.is_active;
    
    const matchesSearch = 
      gym?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      gym?.city?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      gym?.state?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    return matchesStatus && matchesSearch;
  });

  return (
    <>
      <Helmet>
        <title>Gym Management Center | TrainaryGym</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleBasedHeader />
        <div className="page-content-wrapper">
          <DashboardNavigation />
        </div>

        <div className="container mx-auto px-4 lg:px-8 py-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
                Gym Management Center
              </h1>
              <p className="text-muted-foreground">
                View and manage all gym locations across the platform
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={loadGyms}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Refresh
              </Button>
              <Button size="sm" onClick={handleCreateGym}>
                <Icon name="Plus" size={16} className="mr-2" />
                Add New Gym
              </Button>
            </div>
          </div>

          {/* Statistics */}
          {statistics && <GymStatistics statistics={statistics} />}

          {/* Filters and View Toggle */}
          <div className="card mb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Icon 
                  name="Search" 
                  size={18} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
                />
                <input
                  type="text"
                  placeholder="Search by name, city, or state..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* Filter and View Controls */}
              <div className="flex items-center gap-3">
                {/* Status Filter */}
                <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
                  <button
                    onClick={() => setFilterStatus('all')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-250 ${
                      filterStatus === 'all' ?'bg-primary text-primary-foreground shadow-sm' :'text-foreground hover:bg-background'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilterStatus('active')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-250 ${
                      filterStatus === 'active' ?'bg-primary text-primary-foreground shadow-sm' :'text-foreground hover:bg-background'
                    }`}
                  >
                    Active
                  </button>
                  <button
                    onClick={() => setFilterStatus('inactive')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-250 ${
                      filterStatus === 'inactive' ?'bg-primary text-primary-foreground shadow-sm' :'text-foreground hover:bg-background'
                    }`}
                  >
                    Inactive
                  </button>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('cards')}
                    className={`p-2 rounded-md transition-all duration-250 ${
                      viewMode === 'cards' ?'bg-primary text-primary-foreground shadow-sm' :'text-foreground hover:bg-background'
                    }`}
                    title="Card View"
                  >
                    <Icon name="LayoutGrid" size={18} />
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`p-2 rounded-md transition-all duration-250 ${
                      viewMode === 'table' ?'bg-primary text-primary-foreground shadow-sm' :'text-foreground hover:bg-background'
                    }`}
                    title="Table View"
                  >
                    <Icon name="List" size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : filteredGyms?.length === 0 ? (
            <div className="card text-center py-20">
              <Icon name="Building2" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No gyms found</h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery || filterStatus !== 'all' ?'Try adjusting your filters or search query' :'Get started by adding your first gym location'
                }
              </p>
              {!searchQuery && filterStatus === 'all' && (
                <Button onClick={handleCreateGym}>
                  <Icon name="Plus" size={16} className="mr-2" />
                  Add New Gym
                </Button>
              )}
            </div>
          ) : viewMode === 'cards' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGyms?.map(gym => (
                <GymCard
                  key={gym?.id}
                  gym={gym}
                  onEdit={handleEditGym}
                  onToggleStatus={handleToggleStatus}
                />
              ))}
            </div>
          ) : (
            <GymTable
              gyms={filteredGyms}
              onEdit={handleEditGym}
              onToggleStatus={handleToggleStatus}
            />
          )}
        </div>
      </div>
      {/* Modals */}
      {isEditModalOpen && (
        <GymEditModal
          gym={selectedGym}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedGym(null);
          }}
          onSave={handleSaveGym}
        />
      )}
      {isCreateModalOpen && (
        <GymCreateModal
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateNewGym}
        />
      )}
    </>
  );
};

export default GymManagementCenter;
